import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Lock, Mail, Shield } from 'lucide-react'
import { jwtDecode } from 'jwt-decode'
import { authApi } from '../services/authApi'

// JWT Token interface based on the provided sample data
interface JWTToken {
  userId: string
  email: string
  tenantId?: string  // Made optional since it might not be present in all tokens
  type: string
  jti: string
  iat: number
  exp: number
  aud: string
  iss: string
}

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [tokenValid, setTokenValid] = useState(false)
  const [tenantInfo, setTenantInfo] = useState<{name: string, domain: string} | null>(null)
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Validate token on component mount
  useEffect(() => {
    const token = searchParams.get('token')
    
    if (!token) {
      // No token provided, redirect to login
      navigate('/login')
      return
    }

    try {
      // Decode JWT token to get payload
      const tokenData = jwtDecode<JWTToken>(token)
      
      // Debug: Log the decoded token data
      console.log('Decoded JWT token data:', tokenData)
      
      // Validate required fields
      if (!tokenData.userId || !tokenData.email || !tokenData.type) {
        console.error('Missing required fields:', {
          userId: tokenData.userId,
          email: tokenData.email,
          type: tokenData.type
        })
        throw new Error('Invalid token format')
      }

      // Check if token is not expired using exp field
      const currentTimestamp = Math.floor(Date.now() / 1000) // Convert to seconds
      
      console.log('Token expiration check:', {
        currentTimestamp,
        tokenExp: tokenData.exp,
        isExpired: tokenData.exp && currentTimestamp > tokenData.exp,
        timeUntilExpiry: tokenData.exp ? tokenData.exp - currentTimestamp : 'N/A'
      })
      
      if (tokenData.exp && currentTimestamp > tokenData.exp) {
        throw new Error('Token expired')
      }

      // Check if token type is 'invite'
      if (tokenData.type !== 'invite') {
        throw new Error('Invalid token type')
      }

      // Token is valid, set email and tenant info
      setFormData(prev => ({ ...prev, email: tokenData.email }))
      
      // Set tenant info if tenantId is available
      if (tokenData.tenantId) {
        setTenantInfo({
          name: 'Acme Corporation', // This would come from API call with tenantId
          domain: 'acme.com'
        })
      } else {
        setTenantInfo({
          name: 'Organization', // Default name when tenantId is not available
          domain: 'company.com'
        })
      }
      setTokenValid(true)
      
    } catch (err) {
      console.error('Token validation failed:', err)
      setError('Invalid or expired invitation link. Please contact your administrator.')
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    }
  }, [searchParams, navigate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const validateForm = () => {
    if (!formData.email.trim()) {
      setError('Email is required')
      return false
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return false
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setIsLoading(true)
    setError('')

    try {
      const token = searchParams.get('token')
      
      if (!token) {
        setError('Invalid token')
        return
      }

      // Decode JWT token to get user data (for validation only)
      const tokenData = jwtDecode<JWTToken>(token)
      
      // Log token data for debugging (satisfies linter)
      console.log('JWT token validated for user:', tokenData.email)
      
      // Prepare data for API call
      const signupData = {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      }
      
      console.log('Calling signup API with data:', signupData) // For debugging
      
      // Call the actual signup API
      const response = await authApi.signup(signupData)
      
      if (response.success) {
        console.log('Signup successful:', response.data)
        // Navigate to dashboard
        navigate('/')
      } else {
        throw new Error(response.message || 'Signup failed')
      }
    } catch (err: any) {
      console.error('Signup error:', err)
      setError(err.message || 'Account creation failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Show loading while validating token
  if (!tokenValid && !error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Validating invitation...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            You've been invited to join {tenantInfo?.name}
          </p>
        </div>

        {/* Signup Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="off"
                  required
                  disabled
                  value={formData.email}
                  onChange={handleInputChange}
                  className="appearance-none relative block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm cursor-not-allowed"
                  placeholder="john.doe@company.com"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Lock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="off"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="appearance-none relative block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center z-10"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Confirm password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Lock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="off"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="appearance-none relative block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center z-10"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    {error}
                  </h3>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating account...
                </div>
              ) : (
                'Create account'
              )}
            </button>
          </div>

          {/* Back to Login */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Sign in
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Signup
